import clsx from 'clsx';
import type {
  ProvenanceRecord,
  ProvenanceSource,
  ValidRange,
} from '@arcanum-hq/shared-types';
import { Chip, type ChipVariant } from '@/components/ui/Chip';
import {
  deriveVariableStatus,
  type VariableStatus,
} from '@/lib/variable-status';

export interface VariableRowProps {
  /** Internal name (named-range). Stable key + data attribute. */
  name: string;
  /** Display label. Falls back to `name` when not provided. */
  displayName?: string;
  /** Engine value as the raw type (drives the out-of-range guard). */
  value: unknown;
  /** Pre-formatted display string (panel owns formatting consistency). */
  formattedValue: string;
  /**
   * Provenance record from `agent_state.inputProvenance`. `null` when the
   * conversation predates Phase 1's record writeback or when the agent
   * hasn't touched this input yet.
   */
  provenance?: ProvenanceRecord | null;
  /** From `inputDefinition.validRange`. Optional — many inputs have none. */
  validRange?: ValidRange;
  /**
   * When true the row is rendered as a read-only derived value — distinct
   * `text-purple` label color, no status / confidence / out-of-range chrome.
   * Used by the DERIVED bucket (geocode `Location.*` sub-attributes that are
   * computed values, not user-editable inputs).
   * builder-ux-overhaul WS2 Session 4, ADR 0005.
   */
  readOnly?: boolean;
}

const SOURCE_TO_VARIANT: Record<ProvenanceSource, ChipVariant> = {
  user: 'user',
  market_data: 'market',
  default: 'system',
  inferred: 'agent',
  calculated: 'agent',
  scenario: 'agent',
};

const VARIANT_LABEL: Record<ChipVariant, string> = {
  user: 'User',
  market: 'Market',
  system: 'System',
  agent: 'Agent',
  invalid: '—',
};

function sourceToVariant(source: ProvenanceSource | undefined): ChipVariant {
  if (!source) return 'agent';
  return SOURCE_TO_VARIANT[source];
}

function ConfidenceBar({ value }: { value: number }) {
  // Numeric 0-100 bar. Color thresholds align with `<ConfidenceDots>`'s
  // legacy mapping (≥80 → ok, ≥60 → warn, else dim).
  const pct = Math.round(Math.max(0, Math.min(1, value)) * 100);
  const fillColor =
    pct >= 80 ? 'bg-accent' : pct >= 60 ? 'bg-warn' : 'bg-text-dim';
  return (
    <div
      className="flex items-center gap-1"
      title={`Confidence: ${pct}%`}
      data-testid="confidence-bar"
      data-confidence={pct}
    >
      <span className="relative inline-block w-12 h-1.5 bg-panel-border rounded-[1px] overflow-hidden">
        <span
          className={clsx('absolute inset-y-0 left-0', fillColor)}
          style={{ width: `${pct}%` }}
        />
      </span>
      <span className="text-micro font-mono text-text-dim tabular-nums w-7 text-right">
        {pct}%
      </span>
    </div>
  );
}

const STATUS_TOKENS: Record<Exclude<VariableStatus, '—'>, string> = {
  OVERRIDDEN: 'text-warn bg-warn-dim border-warn/15',
  CONFIRMED: 'text-accent bg-accent-dim border-accent/15',
  ACCEPTED: 'text-accent/70 bg-accent-dim/50 border-accent/10',
};

function StatusTag({ status }: { status: VariableStatus }) {
  if (status === '—') {
    return (
      <span
        data-testid="status-tag"
        data-status="none"
        className="text-micro font-mono text-text-dim"
      >
        —
      </span>
    );
  }
  return (
    <span
      data-testid="status-tag"
      data-status={status.toLowerCase()}
      className={clsx(
        'text-micro font-mono font-semibold tracking-[0.5px] px-1.5 py-px rounded-[2px] border',
        STATUS_TOKENS[status]
      )}
    >
      {status}
    </span>
  );
}

function OutOfRangeBadge({ direction }: { direction: 'below' | 'above' }) {
  return (
    <span
      data-testid="out-of-range-badge"
      data-direction={direction}
      className="text-micro font-mono text-danger bg-danger-dim border border-danger/15 rounded-[2px] px-1.5 py-px"
    >
      ⚠ {direction === 'above' ? 'above range' : 'below range'}
    </span>
  );
}

/**
 * Per-input row inside `<VariablesPanel>`. Composes:
 * - Source pill (`<Chip>` reused from Phase 2)
 * - Status tag (`—` / `ACCEPTED` / `CONFIRMED` / `OVERRIDDEN`)
 * - Out-of-range badge (independent of status; dual-render when both apply)
 * - Confidence bar (numeric 0-100, only when provenance carries confidence)
 *
 * When `readOnly` is true (DERIVED bucket — geocode `Location.*` sub-attrs):
 * - Label rendered in `text-purple` to signal a computed/derived value
 * - Status tag / out-of-range badge / confidence bar suppressed (edit-oriented
 *   chrome is irrelevant for computed values that are never user-editable)
 * - `data-read-only="true"` attribute for test-selectable state
 *
 * Status + out-of-range derivation lives in `lib/variable-status.ts` —
 * this component is purely presentational.
 */
export function VariableRow({
  name,
  displayName,
  value,
  formattedValue,
  provenance,
  validRange,
  readOnly = false,
}: VariableRowProps) {
  // Skip status/range derivation entirely for read-only derived rows — these
  // are computed geocode values, not user inputs with ranges or accept flows.
  const result = readOnly
    ? null
    : deriveVariableStatus(provenance ?? null, value, validRange);
  const variant = sourceToVariant(provenance?.source);
  const showConfidence =
    !readOnly &&
    provenance &&
    typeof provenance.confidence === 'number' &&
    Number.isFinite(provenance.confidence);

  return (
    <div
      data-testid="variable-row"
      data-name={name}
      data-bucket-source={provenance?.source ?? 'unknown'}
      data-read-only={readOnly ? 'true' : undefined}
      className="px-4 py-2.5 border-b border-panel-border flex flex-col gap-1"
    >
      <div className="flex justify-between items-center gap-2 min-w-0">
        <span className="flex items-center gap-1.5 min-w-0 flex-1">
          <Chip variant={variant} label={VARIANT_LABEL[variant]} />
          {/* DERIVED rows use text-purple to signal computed/non-editable origin */}
          <span
            className={clsx(
              'text-xs font-mono truncate',
              readOnly ? 'text-purple' : 'text-text-muted'
            )}
          >
            {displayName ?? name}
          </span>
        </span>
        {/* Status tag + out-of-range badge suppressed for read-only derived rows */}
        {!readOnly && result && (
          <span className="shrink-0 flex items-center gap-1.5">
            <StatusTag status={result.status} />
            {result.outOfRange !== 'in-range' && (
              <OutOfRangeBadge direction={result.outOfRange} />
            )}
          </span>
        )}
      </div>
      <div className="flex justify-between items-center gap-2">
        <span className="text-base text-text-bright font-mono font-medium">
          {formattedValue}
        </span>
        {/* Confidence bar suppressed for read-only derived rows */}
        {showConfidence && <ConfidenceBar value={provenance!.confidence} />}
      </div>
    </div>
  );
}
